import type { AnswerT } from '../../../common/types/types';
import { interpolateShapes } from '../../labelling/reducers/utils/interpolation';
import type { AnnotationT } from '../../labelling/types';
import type { AnnotationDataT } from '../../labelling/types/annotations.type';
import { createKeyFrame } from '../../labelling/utils';
import type { AnnotationFrameWriteEntry } from '../atoms/studio-store-write/actions';
import { type AnnotationMeta, type FrameBoundary, type InterpolationTask, type InterpolationTaskEnqueuePayload, BoundaryType } from './types';

// Temporary for now to keep same behaviour as today where everything is
// interpolated at once. Eventually the batch size will be reduced once
// windowing is implemented.
const INTERPOLATION_BATCH_SIZE = 100_000;
const getDynamicAnswersForFrame = (meta: AnnotationMeta, frameIndex: number): AnswerT | undefined => {
  const dynamicAnswerSegment = meta.dynamicAnswers?.find(segment => segment.frameSpan.start <= frameIndex && segment.frameSpan.end >= frameIndex);
  return dynamicAnswerSegment?.answers;
};
export class InterpolationManager {
  private tasks: InterpolationTask[] = [];
  private running = false;
  private readonly getAnnotationMeta: (annotationId: string) => AnnotationMeta | null;
  private readonly bulkWrite: (writes: AnnotationFrameWriteEntry[]) => void;
  constructor(getAnnotationMeta: (annotationId: string) => AnnotationMeta | null, bulkWrite: (writes: AnnotationFrameWriteEntry[]) => void) {
    this.getAnnotationMeta = getAnnotationMeta;
    this.bulkWrite = bulkWrite;
  }
  enqueue(payload: InterpolationTaskEnqueuePayload): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.tasks.push({
        ...payload,
        resolve,
        reject
      });
      if (!this.running) {
        void this.processNext();
      }
    });
  }
  private async processNext(): Promise<void> {
    this.running = true;
    while (this.tasks.length > 0) {
      const task = this.tasks.shift();
      if (!task) {
        break;
      }
      try {
        await this.processTask(task);
        task.resolve();
      } catch (err) {
        task.reject(err);
      }
    }
    this.running = false;
  }
  private async processTask(task: InterpolationTask): Promise<void> {
    const {
      annotationId,
      startFrame,
      endFrame
    } = task;
    const meta = this.getAnnotationMeta(annotationId);
    if (!meta) {
      return;
    }
    await this.processRange(annotationId, meta, startFrame, endFrame);
  }
  private async processRange(annotationId: string, meta: AnnotationMeta, start: FrameBoundary, end: FrameBoundary): Promise<void> {
    const rangeStart = start.index + 1;
    // KeyFrame boundary: end frame is already written — stop before it.
    // SpanBoundary: end frame must also receive the copied geometry — include it.
    const rangeEnd = end.type === BoundaryType.KeyFrame ? end.index - 1 : end.index;
    if (rangeStart > rangeEnd) {
      return;
    }
    const totalFrames = end.index - start.index;
    const computeGeometry = end.type === BoundaryType.KeyFrame ? (i: number): AnnotationDataT => interpolateShapes(meta.annotationType, start.geometry, end.geometry, (i - start.index) / totalFrames) : (_i: number): AnnotationDataT => start.geometry;
    for (let batchStart = rangeStart; batchStart <= rangeEnd; batchStart += INTERPOLATION_BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + INTERPOLATION_BATCH_SIZE - 1, rangeEnd);
      const writes: AnnotationFrameWriteEntry[] = [];
      for (let frameIndex = batchStart; frameIndex <= batchEnd; frameIndex++) {
        const staticAnswers = meta.answers;
        const dynamicAnswers = getDynamicAnswersForFrame(meta, frameIndex);
        const mergedAnswers = staticAnswers && dynamicAnswers ? {
          ...staticAnswers,
          ...dynamicAnswers
        } : staticAnswers || dynamicAnswers;
        const annotation: AnnotationT = ({
          annotationType: meta.annotationType,
          annotationLabel: meta.label,
          data: computeGeometry(frameIndex),
          hidden: false,
          locked: false,
          keyFrame: createKeyFrame(false, 'AUTOMATED'),
          ...(mergedAnswers ? {
            metadata: {
              answers: mergedAnswers
            }
          } : {})
        } as AnnotationT);
        writes.push({
          frameIndex,
          annotationId,
          annotation
        });
      }
      this.bulkWrite(writes);
      await new Promise<void>(resolve => setTimeout(resolve, 0));
    }
  }
}